import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Calendar, Clock, MapPin, User, Video, CheckCircle2,
  XCircle, RefreshCcw, Loader2, AlertCircle, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { MiniCalendar } from '@/components/calendar/MiniCalendar';
import { getErrorMessage } from '@/lib/errors';
import { type EstadoCita, getEstadoCitaInfo } from '@/types/cita';

const publicApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '',
  headers: { 'Content-Type': 'application/json' },
});

type CitaEstado = EstadoCita;

interface CitaPublica {
  id: string;
  estado: CitaEstado;
  tipo: 'presencial' | 'teleconsulta';
  fechaInicio: string;
  fechaFin: string;
  motivo: string | null;
  servicioNombre: string | null;
  motivoCancelacion: string | null;
  medico: { nombre: string };
  clinica: { nombre: string; telefono: string | null; direccion: string | null };
  paciente: { nombre: string };
  teleconsultaUrl: string | null;
}

interface SlotDisponible {
  inicio: string;
  fin: string;
}

// ─── helpers ────────────────────────────────────────────────────────────────

function canConfirm(e: CitaEstado) { return e === 'programada' || e === 'espera'; }
function canReagendar(e: CitaEstado) { return !['completada', 'en_curso', 'no_asistio'].includes(e); }
function canUndoCancel(e: CitaEstado) { return e === 'cancelada'; }

// ─── Diálogo genérico ────────────────────────────────────────────────────────

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

// El token de botón WA es un JWT: header.payload.sig (ya es URL-safe)
// El payload contiene sub=citaId y action=cnf|rgr|ver
function decodeWaToken(encoded: string): { citaId: string; token: string; action: string } {
  try {
    const parts = encoded.split('.');
    if (parts.length !== 3) return { citaId: '', token: '', action: '' };
    const pad = (s: string) => s + '='.repeat((4 - s.length % 4) % 4);
    const payload = JSON.parse(atob(pad(parts[1]).replace(/-/g, '+').replace(/_/g, '/')));
    return { citaId: payload.sub ?? '', token: encoded, action: payload.action ?? '' };
  } catch {
    return { citaId: '', token: '', action: '' };
  }
}

export function DetallesCitaPage() {
  const [params] = useSearchParams();
  const { encoded } = useParams<{ encoded?: string }>();
  const qc = useQueryClient();

  const { citaId, token, action } = encoded
    ? decodeWaToken(encoded)
    : { citaId: params.get('c') ?? '', token: params.get('tk') ?? '', action: params.get('a') ?? '' };

  const [dialog, setDialog]           = useState<'confirm' | 'cancel-error' | 'reagenda' | null>(null);
  const [reagendaDate, setReagendaDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<SlotDisponible | null>(null);

  // ── fetch cita ─────────────────────────────────────────────────────────────

  const { data: cita, isLoading, isError } = useQuery<CitaPublica>({
    queryKey: ['public-cita', citaId, token],
    queryFn: () => publicApi.get(`/api/public/citas/${citaId}?tk=${token}`).then(r => r.data),
    enabled: !!(citaId && token),
    retry: false,
    staleTime: 0,
  });

  // ── auto-open dialog from `a` param ───────────────────────────────────────

  useEffect(() => {
    if (!cita) return;
    if (action === 'cnf' && canConfirm(cita.estado)) setDialog('confirm');
    else if (action === 'rgr' && canReagendar(cita.estado)) setDialog('reagenda');
    else if (action === 'err' && canUndoCancel(cita.estado)) setDialog('cancel-error');
  }, [action, cita]);

  // ── fetch disponibilidad ───────────────────────────────────────────────────

  const duracion = cita
    ? Math.round((new Date(cita.fechaFin).getTime() - new Date(cita.fechaInicio).getTime()) / 60000)
    : 30;

  const { data: slots, isFetching: slotsLoading } = useQuery<SlotDisponible[]>({
    queryKey: ['public-disponibilidad', citaId, reagendaDate?.toISOString()],
    queryFn: () => {
      const fecha = format(reagendaDate!, 'yyyy-MM-dd');
      return publicApi
        .get(`/api/public/citas/${citaId}/disponibilidad?tk=${token}&fecha=${fecha}&duracion=${duracion}`)
        .then(r => r.data?.slots ?? []);
    },
    enabled: !!reagendaDate,
    retry: false,
  });

  // ── mutations ──────────────────────────────────────────────────────────────

  const invalidate = useCallback(
    () => qc.invalidateQueries({ queryKey: ['public-cita', citaId, token] }),
    [qc, citaId, token],
  );

  const confirmarMut = useMutation({
    mutationFn: () => publicApi.post(`/api/public/citas/${citaId}/confirmar?tk=${token}`).then(r => r.data),
    onSuccess: () => { toast.success('Cita confirmada'); setDialog(null); invalidate(); },
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error al confirmar')),
  });

  const cancelarErrorMut = useMutation({
    mutationFn: () => publicApi.post(`/api/public/citas/${citaId}/cancelar-error?tk=${token}`).then(r => r.data),
    onSuccess: () => { toast.success('Cita restaurada a programada'); setDialog(null); invalidate(); },
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error al restaurar cita')),
  });

  const reagendarMut = useMutation({
    mutationFn: (body: { fechaInicio: string; fechaFin: string }) =>
      publicApi.patch(`/api/public/citas/${citaId}/reagendar?tk=${token}`, body).then(r => r.data),
    onSuccess: () => {
      toast.success('Cita reagendada');
      setDialog(null);
      setReagendaDate(null);
      setSelectedSlot(null);
      invalidate();
    },
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error al reagendar')),
  });

  function handleReagendarConfirm() {
    if (!selectedSlot || !reagendaDate) return;
    const toIso = (timeStr: string) => {
      const [h, m] = timeStr.split(':').map(Number);
      const d = new Date(reagendaDate);
      d.setHours(h, m, 0, 0);
      return d.toISOString();
    };
    reagendarMut.mutate({ fechaInicio: toIso(selectedSlot.inicio), fechaFin: toIso(selectedSlot.fin) });
  }

  // ── render ─────────────────────────────────────────────────────────────────

  if (!citaId || !token) {
    return <ErrorState msg="Enlace inválido. Verifica el link que recibiste." />;
  }

  if (isLoading) {
    return (
      <PageShell>
        <div className="flex items-center justify-center py-16 text-gray-400 gap-3">
          <Loader2 className="animate-spin" size={22} />
          <span className="text-sm">Cargando detalles de tu cita…</span>
        </div>
      </PageShell>
    );
  }

  if (isError || !cita) {
    return <ErrorState msg="No pudimos cargar la cita. Es posible que el enlace haya expirado o sea inválido." />;
  }

  const fechaInicio = new Date(cita.fechaInicio);
  const fechaFin    = new Date(cita.fechaFin);

  return (
    <PageShell>
      {/* Header de estado */}
      <div className="flex items-center justify-between mb-5">
        <span className={cn('text-xs font-semibold px-3 py-1 rounded-full', getEstadoCitaInfo(cita.estado).softCls)}>
          {getEstadoCitaInfo(cita.estado).label}
        </span>
        {cita.tipo === 'teleconsulta' && (
          <span className="text-xs text-violet-600 bg-violet-50 px-2 py-1 rounded-full font-medium flex items-center gap-1">
            <Video size={12} /> Videollamada
          </span>
        )}
      </div>

      {/* Detalles */}
      <div className="space-y-3 mb-6">
        <DetailRow icon={<User size={15} />} label="Paciente" value={cita.paciente.nombre} />
        <DetailRow
          icon={<Calendar size={15} />}
          label="Fecha"
          value={format(fechaInicio, "EEEE d 'de' MMMM yyyy", { locale: es })}
        />
        <DetailRow
          icon={<Clock size={15} />}
          label="Hora"
          value={`${format(fechaInicio, 'HH:mm')} – ${format(fechaFin, 'HH:mm')}`}
        />
        <DetailRow icon={<User size={15} />} label="Médico" value={`Dr. ${cita.medico.nombre}`} />
        {cita.servicioNombre && (
          <DetailRow icon={<RefreshCcw size={15} />} label="Servicio" value={cita.servicioNombre} />
        )}
        <DetailRow
          icon={<MapPin size={15} />}
          label="Ubicación"
          value={cita.tipo === 'teleconsulta' ? 'Videollamada' : (cita.clinica.direccion ?? cita.clinica.nombre)}
        />
        {cita.motivoCancelacion && (
          <DetailRow icon={<XCircle size={15} className="text-red-400" />} label="Motivo de cancelación" value={cita.motivoCancelacion} />
        )}
      </div>

      {/* Enlace de teleconsulta */}
      {cita.tipo === 'teleconsulta' && cita.teleconsultaUrl && cita.estado !== 'cancelada' && (
        <a
          href={cita.teleconsultaUrl}
          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold mb-4 transition-colors"
        >
          <Video size={15} />
          Unirse a la videollamada
        </a>
      )}

      {/* Acciones */}
      <div className="flex flex-col gap-2 mt-2">
        {canConfirm(cita.estado) && (
          <button
            onClick={() => setDialog('confirm')}
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition-colors"
          >
            <CheckCircle2 size={15} />
            Confirmar asistencia
          </button>
        )}
        {canReagendar(cita.estado) && (
          <button
            onClick={() => setDialog('reagenda')}
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold transition-colors"
          >
            <RefreshCcw size={15} />
            Reagendar cita
          </button>
        )}
        {canUndoCancel(cita.estado) && (
          <button
            onClick={() => setDialog('cancel-error')}
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-orange-300 hover:bg-orange-50 text-orange-700 text-sm font-semibold transition-colors"
          >
            <RefreshCcw size={15} />
            Fue un error, deseo mantener mi cita
          </button>
        )}
      </div>

      {/* ── Diálogos ─────────────────────────────────────────────────────── */}

      {dialog === 'confirm' && (
        <Modal title="Confirmar asistencia" onClose={() => setDialog(null)}>
          <p className="text-sm text-gray-600 mb-5">
            ¿Confirmas que asistirás a tu cita el{' '}
            <strong>{format(fechaInicio, "EEEE d 'de' MMMM", { locale: es })}</strong> a las{' '}
            <strong>{format(fechaInicio, 'HH:mm')}</strong>?
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setDialog(null)}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={() => confirmarMut.mutate()}
              disabled={confirmarMut.isPending}
              className="flex-1 flex items-center justify-center gap-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50"
            >
              {confirmarMut.isPending && <Loader2 size={13} className="animate-spin" />}
              Confirmar
            </button>
          </div>
        </Modal>
      )}

      {dialog === 'cancel-error' && (
        <Modal title="Restaurar cita" onClose={() => setDialog(null)}>
          <p className="text-sm text-gray-600 mb-5">
            ¿Deseas restaurar tu cita a su estado anterior? Quedará marcada como <strong>Programada</strong>.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setDialog(null)}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={() => cancelarErrorMut.mutate()}
              disabled={cancelarErrorMut.isPending}
              className="flex-1 flex items-center justify-center gap-1 py-2.5 rounded-xl bg-orange-600 hover:bg-orange-500 text-white text-sm font-semibold disabled:opacity-50"
            >
              {cancelarErrorMut.isPending && <Loader2 size={13} className="animate-spin" />}
              Restaurar cita
            </button>
          </div>
        </Modal>
      )}

      {dialog === 'reagenda' && (
        <ReagendaDialog
          onClose={() => { setDialog(null); setReagendaDate(null); setSelectedSlot(null); }}
          date={reagendaDate}
          onDateChange={d => { setReagendaDate(d); setSelectedSlot(null); }}
          slots={slots ?? []}
          slotsLoading={slotsLoading}
          selectedSlot={selectedSlot}
          onSlotSelect={setSelectedSlot}
          onConfirm={handleReagendarConfirm}
          isPending={reagendarMut.isPending}
        />
      )}
    </PageShell>
  );
}

// ─── sub-components ──────────────────────────────────────────────────────────

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-start pt-10 pb-16 px-4">
      <div className="w-full max-w-sm">
        <p className="text-center text-gray-400 text-xs font-medium tracking-wide uppercase mb-6">Anik · Detalles de cita</p>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">{children}</div>
      </div>
    </div>
  );
}

function ErrorState({ msg }: { msg: string }) {
  return (
    <PageShell>
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <AlertCircle className="text-red-400" size={32} />
        <p className="text-sm text-gray-600">{msg}</p>
      </div>
    </PageShell>
  );
}

function DetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-gray-400 mt-0.5 shrink-0">{icon}</span>
      <div>
        <p className="text-[10px] text-gray-400 uppercase font-medium tracking-wide">{label}</p>
        <p className="text-sm text-gray-800 capitalize">{value}</p>
      </div>
    </div>
  );
}

interface ReagendaDialogProps {
  onClose: () => void;
  date: Date | null;
  onDateChange: (d: Date) => void;
  slots: SlotDisponible[];
  slotsLoading: boolean;
  selectedSlot: SlotDisponible | null;
  onSlotSelect: (s: SlotDisponible) => void;
  onConfirm: () => void;
  isPending: boolean;
}

function ReagendaDialog({
  onClose, date, onDateChange, slots, slotsLoading, selectedSlot, onSlotSelect, onConfirm, isPending,
}: ReagendaDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-gray-800 text-sm">Elegir nueva fecha y hora</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {/* mini calendario */}
          <MiniCalendar date={date ?? new Date()} onDateChange={onDateChange} />

          {/* slots */}
          {date && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">
                Horarios disponibles — {format(date, "d 'de' MMMM", { locale: es })}
              </p>
              {slotsLoading ? (
                <div className="flex items-center gap-2 text-gray-400 py-4 justify-center">
                  <Loader2 size={16} className="animate-spin" />
                  <span className="text-xs">Cargando horarios…</span>
                </div>
              ) : slots.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">Sin disponibilidad para este día.</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {slots.map(s => (
                    <button
                      key={s.inicio}
                      onClick={() => onSlotSelect(s)}
                      className={cn(
                        'py-2 rounded-xl text-sm font-medium border transition-colors',
                        selectedSlot?.inicio === s.inicio
                          ? 'bg-primary-600 border-primary-600 text-white'
                          : 'border-gray-200 text-gray-700 hover:border-primary-400 hover:bg-primary-50',
                      )}
                    >
                      {s.inicio.slice(0, 5)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* footer */}
        <div className="px-5 py-4 border-t border-gray-100 shrink-0 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={!selectedSlot || isPending}
            className="flex-1 flex items-center justify-center gap-1 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-500 text-white text-sm font-semibold disabled:opacity-40"
          >
            {isPending && <Loader2 size={13} className="animate-spin" />}
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}
